import React from 'react';

import { Box } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import webstyled from 'styled-components';
import styled from 'styled-components/native';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { Loyalty } from 'components/app-header/loyalty';
import { HamburgerButton } from 'components/app-header/mobile-header/hamburger-button';
import defaultConfig from 'components/app-header/styled';
import StyledLoadingIndicator from 'components/app-header/styled-loading-indicator';
import { AppSubheader } from 'components/app-subheader';
import Currency from 'components/currency';
import { DeliveryBannerContainer } from 'components/delivery-banner';
import { Drawer } from 'components/drawer';
import IconProfileTH from 'components/icons/profile-th';
import LoadingAnimation from 'components/loading-animation';
import SkipToContent from 'components/skip-content';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import useLocalizedRoutesForServiceModeHeaderDisplay from 'hooks/use-localized-routes-for-service-mode-header-display';
import useMediaQuery from 'hooks/use-media-query';
import useRoutes from 'hooks/use-routes';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoadingIndicator } from 'state/loading';
import { useLocationContext } from 'state/location';
import { useMobileHeaderBackButton, useMobileHeaderComponent } from 'state/mobile-header-nav';
import { usePaymentContext } from 'state/payment';
import { IPaymentMethod } from 'state/payment/types';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { delayUnmountHOC } from 'utils/delay-unmount-hoc';
import { hasPageBeenOpenedFromApp } from 'utils/params';
import { routes } from 'utils/routing';

import SignupMobileHeader from '../signup-mobile-header';
import { StyledCenterSpacer, StyledRightSpacer } from '../styled/base';

import { HeaderLogo } from './header-logo';
import { IMobileHeaderProps } from './types';

// The types for react native do not support `dataSet` yet, but
// it is the required way to pass data-* attributes to the node.
// And we are using this mechanism to ensure these nodes are rendered
// during the pre-render, but only visible for the correct screen size.
const onlyRenderInMobile = () =>
  ({
    dataSet: { mediaquery: 'mobile' },
  } as any);

// The absolute position pops it out of the flex flow
// and this allows our centered logo to be truly centered and not offset by the menu item
const MenuButtonContainer = styled.View`
  position: absolute;
  flex-shrink: 1;
`;

const MobileHeaderContainer = styled.SafeAreaView`
  padding-bottom: 0px;
  width: 100%;
  z-index: ${Styles.zIndex.top};
  background: ${p => p.theme.token('top-nav-bar-background-color')};
`;

const Header = styled.View`
  align-items: center;
  color: ${Styles.color.white};
  height: ${Styles.layout.navHeight.mobile};
  padding: 16px;
  flex-direction: row;
  justify-content: space-between;
  text-align: center;
  width: 100%;
`;

const MobileItemContainer = styled.View`
  flex: 1;
  align-items: flex-end;
  flex-shrink: 0;
  max-width: 58%;
`;

const StyledTHCenterSpacer = webstyled(StyledCenterSpacer)`
  justify-content: flex-start;
  flex-shrink: 1;
  min-width: 0;
`;

const AuthButtonContainer = webstyled(Box)`
  flex-shrink: 0;
  max-width: 100%;
`;

const LinkNoDecoration = styled(Link)`
  ::before {
    color: transparent;
  }
  &:active,
  &:hover,
  &:link,
  &:visited {
    color: inherit;
  }
`;

const StyledProfile = webstyled(IconProfileTH)`
  height: 24px;
  fill: ${Styles.color.white};
`;

const PaddedText = styled.Text`
  margin-bottom: 3px;
`;

const StyledAuthButton = webstyled(ActionButton)`
  padding: 0.375rem 0.75rem;
`;
const DelayedUnmountDrawer = delayUnmountHOC(Drawer);
const StandardMobileHeader = ({
  serviceMode,
  userAddress,
  cateringPickupDateTime,
}: IMobileHeaderProps) => {
  const LoadingIndicator = useLoadingIndicator();
  const {
    location: { pathname },
  } = useLocationContext();
  const { formatMessage } = useIntl();
  const { isStoreOpenAndAvailable } = useStoreContext();
  const {
    getBalanceFromPaymentMethods,
    paymentMethods,
    loading: paymentLoading,
  } = usePaymentContext();
  const { getLocalizedRouteForPath } = useRoutes();
  const { showCardTab, showRewardsTab, hideDrawer, isShowingDrawer } = useUIContext();
  const { loading: loyaltyLoading, loyaltyPointsFraction } = useLoyaltyActiveCard();
  // determines whether service mode is shown using current window location and service mode
  const showServiceMode = useLocalizedRoutesForServiceModeHeaderDisplay(serviceMode);
  const enableAccountInFooter = useFlag(LaunchDarklyFlag.ENABLE_ACCOUNT_IN_FOOTER);
  const isHamburgerMenuEnabled = useFlag(LaunchDarklyFlag.ENABLE_HAMBURGER_MENU_ON_MOBILE);
  const enableBottomServiceMode = useFlag(LaunchDarklyFlag.ENABLE_BOTTOM_SERVICE_MODE);
  const isUnifiedAuthEntry = useFlag(LaunchDarklyFlag.EXP_UNIFIED_AUTH_ENTRY_WEB);
  const authEntryButtonLabel = formatMessage({
    id: isUnifiedAuthEntry ? 'joinNowOrSignIn' : 'logIn',
  });
  const BackButtonComponent = useMobileHeaderBackButton();
  const { isAuthenticated } = useAuthContext();

  let additionalButtons;
  const isMobile = useMediaQuery('mobile');

  if (isAuthenticated() && !isMobile) {
    if (paymentLoading || loyaltyLoading) {
      additionalButtons = <LoadingAnimation />;
    } else {
      const prepaidPaymentMethod = paymentMethods.find(
        (method: IPaymentMethod) => (method.prepaid as unknown) as boolean
      );
      let balanceAmount = 0;
      if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
        balanceAmount = getBalanceFromPaymentMethods(prepaidPaymentMethod!);
      }
      additionalButtons = (
        <>
          <ActionButton
            variant={defaultConfig.linkVariant}
            transparent
            onClick={showRewardsTab}
            data-testid="my-rewards-link"
            title={formatMessage({ id: 'myRewards' })}
          >
            <PaddedText>{formatMessage({ id: 'visits' })}</PaddedText>
            <span>{loyaltyPointsFraction}</span>
          </ActionButton>
          <ActionButton
            variant={defaultConfig.linkVariant}
            transparent
            onClick={showCardTab}
            data-testid="tim-card-link"
            title="Tim Card"
          >
            <PaddedText>{formatMessage({ id: 'thTimsRewards' })}</PaddedText>
            <Currency amount={balanceAmount} />
          </ActionButton>
        </>
      );
    }
  }

  const LeftSideComponent =
    BackButtonComponent || (isHamburgerMenuEnabled ? HamburgerButton : () => null);

  const shouldShowSignupMobileHeader =
    pathname === formatMessage({ id: 'routes.signUp' }) ||
    pathname === formatMessage({ id: 'routes.signIn' }) ||
    pathname === routes.confirmOtp;

  if (shouldShowSignupMobileHeader) {
    return <SignupMobileHeader />;
  }

  return (
    <MobileHeaderContainer
      {...onlyRenderInMobile()}
      aria-label={formatMessage({ id: 'primaryNavLinks' })}
    >
      <Header>
        <MenuButtonContainer>
          <LeftSideComponent />
        </MenuButtonContainer>
        <SkipToContent />
        <StyledTHCenterSpacer>
          <HeaderLogo />
        </StyledTHCenterSpacer>
        <MobileItemContainer>
          {additionalButtons}
          {isAuthenticated() ? (
            !enableAccountInFooter && (
              <LinkNoDecoration
                data-testid="mobile-nav-account-link"
                to={getLocalizedRouteForPath(routes.account)}
              >
                <StyledProfile />
              </LinkNoDecoration>
            )
          ) : (
            <StyledRightSpacer>
              {/* will not show SignUp button inside eco app */}
              {!hasPageBeenOpenedFromApp() && (
                <AuthButtonContainer>
                  <StyledAuthButton
                    variant={ActionButtonVariants.INVERSE}
                    size={ActionButtonSizes.SMALL}
                    to={routes.signIn}
                    data-testid="mobile-nav-sign-in-link"
                  >
                    {authEntryButtonLabel}
                  </StyledAuthButton>
                </AuthButtonContainer>
              )}
            </StyledRightSpacer>
          )}
        </MobileItemContainer>
        <DelayedUnmountDrawer
          onDismiss={hideDrawer}
          delayTime={500}
          isVisible={isShowingDrawer}
          disableScroll
        >
          <Loyalty onDismiss={hideDrawer} />
        </DelayedUnmountDrawer>
        <StyledLoadingIndicator as={LoadingIndicator} hasStoreSelection={isStoreOpenAndAvailable} />
      </Header>
      {!enableBottomServiceMode && <DeliveryBannerContainer />}
      {showServiceMode && (
        <AppSubheader
          serviceMode={serviceMode!}
          userAddress={userAddress}
          cateringPickupDateTime={cateringPickupDateTime}
        />
      )}
    </MobileHeaderContainer>
  );
};

export default function MobileHeader(props: IMobileHeaderProps) {
  const HeaderComponent = useMobileHeaderComponent();

  return HeaderComponent ? <HeaderComponent /> : <StandardMobileHeader {...props} />;
}
